import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig, FormlyConfig } from "@ngx-formly/core";
import { InputTextModule } from "primeng/inputtext";
import { Subscription } from "rxjs";

/* 
  props.type: "email" | "number" | null (for text)

  Example usage:
  // Regular text input
  {
    key: "firstName",
    type: "text",
    props: {
      required: true,
      label: {
        en: "First name",
        fi: "Etunimi",
        sv: "Förnamn",
      },                                  // Optional
      placeholder: {
        en: "Enter your first name",
        fi: "Syötä etunimesi",
        sv: "Ange ditt förnamn",
      },                                  // Optional
      description: {
        en: "Enter your first name",
        fi: "Syötä etunimesi",
        sv: "Ange ditt förnamn",
      },                                  // Optional
    },
  },

  // Email input
  {
    key: "email",
    type: "text",
    props: {
      required: true,
      label: {
        en: "Email address",
        fi: "Sähköpostiosoite",
        sv: "E-postadress",
      },                                  // Optional
      placeholder: {
        en: "Enter your email address",
        fi: "Syötä sähköpostiosoitteesi",
        sv: "Ange din e-postadress",
      },                                  // Optional
      description: {
        en: "Enter your first name",
        fi: "Syötä etunimesi",
        sv: "Ange ditt förnamn",
      },                                  // Optional
    },
    validators: {
      validation: ["email"],
    },
  },

  // Number input
  {
    key: "number",
    type: "text",
    props: {
      required: true,
      label: {
        en: "Number",
        fi: "Numero",
        sv: "Nummer",
      },                                  // Optional
      placeholder: {
        en: "Enter a number",
        fi: "Syötä numero",
        sv: "Ange ett nummer",
      },                                  // Optional
      description: {
        en: "Enter your first name",
        fi: "Syötä etunimesi",
        sv: "Ange ditt förnamn",
      },                                  // Optional
    },
    validators: {
      validation: ["number"],
    },
  },

  ////////////////////////////////

  JSON FORMAT:

  Regular text input:
  {
    "key": "firstName",
    "type": "text",
    "props": {
      "required": true,
      "label": {
        "en": "First name",
        "fi": "Etunimi",
        "sv": "Förnamn"
      },
      "placeholder": {
        "en": "Enter your first name",
        "fi": "Syötä etunimesi",
        "sv": "Ange ditt förnamn"
      },
      "description": {
        "en": "Enter your first name",
        "fi": "Syötä etunimesi",
        "sv": "Ange ditt förnamn"
      }
    }
  }

  Email input:
  {
    "key": "email",
    "type": "text",
    "props": {
      "required": true,
      "label": {
        "en": "Email address",
        "fi": "Sähköpostiosoite",
        "sv": "E-postadress"
      },
      "placeholder": {
        "en": "Enter your email address",
        "fi": "Syötä sähköpostiosoitteesi",
        "sv": "Ange din e-postadress"
      },
      "description": {
        "en": "Enter your first name",
        "fi": "Syötä etunimesi",
        "sv": "Ange ditt förnamn"
      }
    },
    "validators": {
      "validation": ["email"]
    }
  }

  Number input:
  {
    "key": "number",
    "type": "text",
    "props": {
      "required": true,
      "label": {
        "en": "Number",
        "fi": "Numero",
        "sv": "Nummer"
      },
      "placeholder": {
        "en": "Enter a number",
        "fi": "Syötä numero",
        "sv": "Ange ett nummer"
      },
      "description": {
        "en": "Enter your first name",
        "fi": "Syötä etunimesi",
        "sv": "Ange ditt förnamn"
      }
    },
    "validators": {
      "validation": ["number"]
    }
  }

  */

@Component({
  selector: "app-text",
  templateUrl: "./text.type.html",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule],
})
export class TextInputComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  inputFormControl = new FormControl<string | number | null>(null);
  private subscription?: Subscription;
  private validationSubscription?: Subscription;
  /** True while we push our own value up to `formControl`, so the echo isn't copied back into the input. */
  private isWritingToFormControl = false;
  private formlyConfig = inject(FormlyConfig);

  get inputType(): string {
    return this.props["type"] || "text";
  }

  get isNumberType(): boolean {
    return this.inputType === "number";
  }

  get isEmailType(): boolean {
    return this.inputType === "email";
  }

  get hasNumberValidator(): boolean {
    return this.field?.validators?.validation?.includes("number") ?? false;
  }

  constructor(public translocoService: TranslocoService) {
    super();
  }

  ngOnInit(): void {
    this.inputFormControl.setValue(this.toInputValue(this.formControl.value));

    const validators = [];
    if (this.props.required) {
      validators.push(Validators.required);
    }

    this.inputFormControl.setValidators(validators);
    this.inputFormControl.updateValueAndValidity();

    if (this.props.disabled || this.formControl.disabled) {
      this.inputFormControl.disable();
    }

    this.subscription = this.inputFormControl.valueChanges.subscribe((val) => {
      let processedValue = val ?? null;

      // Convert to number if number validator is present
      if (this.hasNumberValidator && val !== null && val !== "") {
        // Comma is not allowed in numbers, so we replace it with a dot
        if (typeof val === "string" && val.includes(",")) {
          val = val.replace(",", ".");
        }
        const numericValue = Number(val);
        if (!isNaN(numericValue)) {
          processedValue = numericValue;
        }
      }

      this.isWritingToFormControl = true;
      this.formControl.setValue(processedValue);
      this.isWritingToFormControl = false;
      this.formControl.markAsDirty();
      this.syncValidity();
    });

    // Pick up changes made from outside, e.g. a form reset or a model swap.
    this.subscription.add(
      this.formControl.valueChanges.subscribe((value) => {
        const inputValue = this.toInputValue(value);
        if (!this.isWritingToFormControl && inputValue !== this.inputFormControl.value) {
          this.inputFormControl.setValue(inputValue, { emitEvent: false });
        }
      }),
    );

    this.formControl.statusChanges.subscribe(() => {
      if (this.formControl.disabled && !this.inputFormControl.disabled) {
        this.inputFormControl.disable({ emitEvent: false });
      } else if (!this.formControl.disabled && this.inputFormControl.disabled && !this.props.disabled) {
        this.inputFormControl.enable({ emitEvent: false });
      }
    });

    this.syncValidity();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.validationSubscription?.unsubscribe();
  }

  public onBlur(): void {
    this.formControl.markAsTouched();
    this.inputFormControl.markAsTouched();
    this.syncValidity();
  }

  public isFieldInvalid(): boolean {
    const inputInvalid =
      this.inputFormControl.invalid && (this.inputFormControl.dirty || this.inputFormControl.touched);
    const formlyInvalid = this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
    return inputInvalid || formlyInvalid;
  }

  public getErrorMessage(): string {
    // Check internal inputFormControl errors first (for built-in validators)
    if (this.inputFormControl.hasError("required")) {
      return this.translocoService.translate("app.formly-types.validationMessages.fieldRequired");
    }

    // For Formly validators, use the configured validation messages
    if (this.formControl.errors) {
      const errorKeys = Object.keys(this.formControl.errors);
      if (errorKeys.length > 0) {
        const errorKey = errorKeys[0];

        // Get the validation message from Formly config
        const validationMessage = this.formlyConfig.getValidatorMessage(errorKey);
        if (validationMessage) {
          return this.translocoService.translate(validationMessage as string);
        }
        return this.translocoService.translate("app.formly-types.validationMessages.invalidInput");
      }
    }

    return this.translocoService.translate("app.formly-types.validationMessages.invalidInput");
  }

  private toInputValue(value: string | number | null | undefined): string | number {
    // If we have a number validator and the value is a number, convert it to string for the input
    return this.hasNumberValidator && typeof value === "number" ? value.toString() : (value ?? "");
  }

  private syncValidity(): void {
    if (this.inputFormControl.invalid) {
      this.formControl.setErrors({ invalid: true });
    } else {
      const { ...errors } = this.formControl.errors ?? {};
      this.formControl.setErrors(Object.keys(errors).length ? errors : null);
    }
  }
}
